import React, { RefObject, useCallback, useState } from 'react';
import { Bottom, ExpiryOption, FirstOption, Icon, LeftOptionDiv, Name, Option, Options } from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import Button from '../../button';
import copy from 'clipboard-copy';
import { captureException } from '../../../services/logService';
import { dispatchProjectSuccess, updateDocumentData } from '../../../services/projectServices';
import { REACT_APP_BLOCKSUITE_URL } from '../../../global/environment';
import { Dropdown, DropdownItem } from '../../dropdown';
import { rootStore } from '../../../mobx/rootStore';
import CalendarPopUpV2 from '../../calendarPopUp';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import ModalHeader from '../modalHeader';
import SwitchInput from '../../input/switchInput';

interface Props {
  onClickCancel: () => void;
  editorIframe: RefObject<HTMLIFrameElement>;
  docTitle: string;
  docId: string;
  isEdge: boolean;
}
const ShareDocModal = (props: Props) => {
  const { onClickCancel, isEdge } = props;

  // mobx variable
  const { projectStore } = rootStore;
  const { docData } = projectStore;

  //Other variable
  const [isSwitchOn, setIsSwitchOn] = useState(docData?.isPublic);

  const handleSwitchChange = async () => {
    try {
      setIsSwitchOn(!isSwitchOn);
      const payload = {
        IsPublic: !isSwitchOn
      };
      await updateDocumentData(docData?.id, payload);
      rootStore.projectStore.updateDocData('isPublic', !isSwitchOn);
    } catch (error) {
      captureException(error);
    }
  };

  const exportToPdf = () => {
    props.editorIframe.current?.contentWindow?.postMessage(
      { type: 'export', payload: 'pdf' },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  };
  const exportToHtml = () => {
    props.editorIframe.current?.contentWindow?.postMessage(
      { type: 'export', payload: 'html' },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  };
  const exportToMarkdown = () => {
    props.editorIframe.current?.contentWindow?.postMessage(
      { type: 'export', payload: 'markdown' },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  };
  const exportToPng = () => {
    props.editorIframe.current?.contentWindow?.postMessage(
      { type: 'export', payload: 'png' },
      `${REACT_APP_BLOCKSUITE_URL}`
    );
  };

  const onClickPrivateCopy = useCallback(async () => {
    try {
      const url = window.location.href;
      await copy(url);
      dispatchProjectSuccess('Client invite url copied successfully!');
    } catch (error) {
      captureException(error);
    }
  }, []);

  const onClickPublicCopy = useCallback(async () => {
    try {
      const url = `https://share.teamcamp.app/docs/${props.docId}`;
      await copy(url);
      dispatchProjectSuccess('Client invite url copied successfully!');
    } catch (error) {
      captureException(error);
    }
  }, [props.docId]);

  return (
    <>
      <ModalHeader title='Share this doc' description={`Sharing “${props.docTitle}”`} onClose={onClickCancel} />
      <Bottom>
        <FirstOption className={isSwitchOn ? 'active' : ''}>
          <Options>
            <LeftOptionDiv>
              <Icon>
                <SVGIcon
                  name='globe-icon'
                  width={ICON_SIZE.LG}
                  height={ICON_SIZE.LG}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-primary)'
                />
              </Icon>
              <Name>Share link with anyone</Name>
            </LeftOptionDiv>
            <SwitchInput onChange={handleSwitchChange} checked={isSwitchOn} />
          </Options>
          {isSwitchOn && (
            <>
              <Button
                title='Copy Public Link'
                variant={BUTTON_TYPE.PRIMARY}
                className='btn'
                onClick={onClickPublicCopy}
              />
              <ExpiryOption>
                <Name>Expire link</Name>
                <CalendarPopUpV2 isShareDocModel={true} />
              </ExpiryOption>
              <ExpiryOption className='public-duplication'>
                <Name>Public duplication</Name>
                <SwitchInput />
              </ExpiryOption>
            </>
          )}
        </FirstOption>
        <Option>
          <LeftOptionDiv>
            <Icon>
              <SVGIcon
                name='copy-link-icon'
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-primary)'
              />
            </Icon>
            <Name>Private link</Name>
          </LeftOptionDiv>
          <Button title='Copy link' variant={BUTTON_TYPE.SECONDARY} size={'small'} onClick={onClickPrivateCopy} />
        </Option>
        {!isEdge && (
          <Option>
            <LeftOptionDiv>
              <Icon>
                <SVGIcon
                  name='upload-icon'
                  width={ICON_SIZE.MD}
                  height={ICON_SIZE.MD}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-primary)'
                />
              </Icon>
              <Name>Export doc</Name>
            </LeftOptionDiv>
            <Dropdown
              content={<Button title='Export' variant={BUTTON_TYPE.SECONDARY} size={'small'} />}
              trigger='click'>
              <DropdownItem key={1} label='PDF' onClick={exportToPdf} />
              <DropdownItem key={2} label='HTML' onClick={exportToHtml} />
              <DropdownItem key={3} label='Markdown' onClick={exportToMarkdown} />
              <DropdownItem key={4} label='PNG' onClick={exportToPng} />
            </Dropdown>
          </Option>
        )}
      </Bottom>
    </>
  );
};
export default observer(ShareDocModal);
